import type { WalletOffer } from "@b2bportal/wallet-offer-api";
import { getObjectKeysAsObject } from "@checkout/helpers";
import { ParentState } from "@checkout/types";
import { WalletVoucherStatusEnum } from "@hopper-b2b/types";
import { assign, type DoneInvokeEvent } from "xstate";
import type {
  ApplyWalletOfferEvent,
  SetWalletCreditAmountEvent,
  SetWalletCreditBalanceEvent,
  SetWalletOfferErrorEvent,
} from "./events";
import {
  OfferPaymentState,
  TravelCreditPaymentState,
  type WalletPaymentParentContext,
} from "./types";
import {
  applyOfferToCartUpdateParams,
  findOfferPaymentId,
  getBestWalletOffer,
  removeOfferFromCartUpdateParams,
} from "./utils";

export const actions = {
  setWalletCreditPayAmount: assign(
    (ctx: WalletPaymentParentContext, event: SetWalletCreditAmountEvent) => {
      ctx[TravelCreditPaymentState].creditPaymentAmount = event.amount;
      return ctx;
    }
  ),
  setWalletCreditBalance: assign(
    (ctx: WalletPaymentParentContext, event: SetWalletCreditBalanceEvent) => {
      ctx[TravelCreditPaymentState].creditBalance = event.value;
      return ctx;
    }
  ),
  setWalletOffers: assign(
    (
      context: WalletPaymentParentContext,
      event: DoneInvokeEvent<WalletOffer[]>
    ) => {
      const offers = event.data;
      context[OfferPaymentState].visited = true;
      context[OfferPaymentState].fetchedOffers = true;
      if (offers.length > 0) {
        context[OfferPaymentState].offers = offers;
        context[OfferPaymentState].selectedOffer = getBestWalletOffer(offers);
        context[OfferPaymentState].offerApplied = true;
        context[OfferPaymentState].offerStatus =
          WalletVoucherStatusEnum.DEFAULT_OPT_IN;
      }

      return context;
    }
  ),
  setWalletOfferError: assign(
    (ctx: WalletPaymentParentContext, event: SetWalletOfferErrorEvent) => {
      ctx[OfferPaymentState].error = event.error;
      return ctx;
    }
  ),
  applyWalletOffer: assign(
    (ctx: WalletPaymentParentContext, event: ApplyWalletOfferEvent) => {
      const offerState = ctx[OfferPaymentState];
      const offer = offerState.offers?.find((o) => o.id === event.offerId);
      if (offer == null) {
        return ctx;
      }

      // Single applied offer: swapping removes the previous one in the same
      // quote/update.
      const previousOffer = offerState.selectedOffer;
      let cartUpdate = ctx[ParentState.cartUpdate];
      if (
        previousOffer != null &&
        previousOffer.id !== offer.id &&
        offerState.offerApplied
      ) {
        // Use the BE-assigned payment ID from the breakdown, not the offer JWT.
        const paymentId =
          findOfferPaymentId(ctx[ParentState.cartQuote].quoteBreakdown) ??
          previousOffer.id;
        cartUpdate = removeOfferFromCartUpdateParams(cartUpdate, paymentId, {
          isApplied: true,
        });
      }

      ctx[ParentState.cartUpdate] = applyOfferToCartUpdateParams(
        cartUpdate,
        offer.id
      );
      offerState.selectedOffer = offer;
      offerState.offerApplied = true;
      offerState.offerStatus = WalletVoucherStatusEnum.EXPLICITLY_ACCEPTED;
      return ctx;
    }
  ),
  removeWalletOffer: assign((ctx: WalletPaymentParentContext) => {
    const offerState = ctx[OfferPaymentState];
    const offer = offerState.selectedOffer;
    if (offer == null) {
      return ctx;
    }

    // Use the BE-assigned payment ID from the breakdown, not the offer JWT.
    const paymentId =
      findOfferPaymentId(ctx[ParentState.cartQuote].quoteBreakdown) ?? offer.id;

    ctx[ParentState.cartUpdate] = removeOfferFromCartUpdateParams(
      ctx[ParentState.cartUpdate],
      paymentId,
      { isApplied: Boolean(offerState.offerApplied) }
    );
    // selectedOffer is kept for tracking only (which offer was declined);
    // consumers must gate on offerApplied — see getAppliedWalletOffer.
    offerState.offerApplied = false;
    offerState.offerStatus = WalletVoucherStatusEnum.EXPLICITLY_DECLINED;
    return ctx;
  }),
  /**
   * cartUpdate entry action: auto-applies the selected offer on the initial
   * pass. No-op if off, unapplied, or already queued.
   */
  addAppliedWalletOfferToCartUpdate: assign(
    (ctx: WalletPaymentParentContext) => {
      const offerState = ctx[OfferPaymentState];
      const offer = offerState?.selectedOffer;
      if (
        !ctx.featureFlags?.enableWalletOffers ||
        !offerState?.offerApplied ||
        offer == null
      ) {
        return ctx;
      }

      ctx[ParentState.cartUpdate] = applyOfferToCartUpdateParams(
        ctx[ParentState.cartUpdate],
        offer.id
      );
      return ctx;
    }
  ),
};

export const ActionTypes = getObjectKeysAsObject(actions);
